/**
 * Hardcoded release years for the FE display names shown in Database Access sidebar
 * and reused on the Library page (Dataset column). Match by publicationId.
 */
export const RELEASE_DISPLAY_NAME_YEARS = {
  latest: '2026',
  previous: '2025'
}

/**
 * API publications often append a refresh month, e.g. "2024 YE Total Financial Assets - 2025 April".
 * Non-admin UI shows the leading segment only (strip " - YYYY Month" at the end).
 */
export function stripPublicationDisplayNameSuffix(label) {
  if (!label || typeof label !== 'string') return ''
  const month =
    'January|February|March|April|May|June|July|August|September|October|November|December|' +
    'Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec'
  return label.replace(new RegExp(`\\s*-\\s*\\d{4}\\s+(?:${month})\\s*$`, 'i'), '').trim()
}

/**
 * Display name for non–GGC-admin users (Database Access sidebar, Library map, etc.).
 * Prefer API `displayName` / `name` with {@link stripPublicationDisplayNameSuffix} applied.
 * If those are missing, falls back to "{year} YE {dataset}" using {@link RELEASE_DISPLAY_NAME_YEARS}.
 * @param {{ publicationStatus?: { name?: string }, dataset?: { displayName?: string, name?: string }, displayName?: string, name?: string }} publication - API publication object
 * @returns {string} e.g. "2024 YE Full Granularity" from "2024 YE Full Granularity - 2025 April"
 */
export function getReleaseDisplayName(publication) {
  if (!publication) return ''
  const raw = publication.displayName ?? publication.name ?? ''
  const fromApi = stripPublicationDisplayNameSuffix(raw)
  if (fromApi) return fromApi

  const status = (publication.publicationStatus?.name ?? '').toLowerCase()
  const isPrevious = status === 'previous release'
  const year = isPrevious ? RELEASE_DISPLAY_NAME_YEARS.previous : RELEASE_DISPLAY_NAME_YEARS.latest
  const baseName = publication.dataset?.displayName ?? publication.dataset?.name ?? 'Dataset'
  return `${year} YE ${baseName}`
}

/**
 * Sidebar release sections (Database Access left pane + Dataset Info popup headings).
 * Single source of truth with {@link RELEASE_DISPLAY_NAME_YEARS}.
 */
export const RELEASE_SECTIONS = [
  {
    key: 'latest',
    heading: `Latest Release (As of June ${RELEASE_DISPLAY_NAME_YEARS.latest})`,
    statusFilter: 'live'
  },
  {
    key: 'previous',
    heading: `Previous Release (As of June ${RELEASE_DISPLAY_NAME_YEARS.previous})`,
    statusFilter: 'previous'
  }
]

const DATASET_BASE_NAMES = ['Full Granularity', 'Segment Granularity', 'Total Financial Assets']

/**
 * All release menu items for the Dataset Info popup (non-admin view).
 * Built from RELEASE_SECTIONS x DATASET_BASE_NAMES so labels stay in sync with the year constants.
 */
export const RELEASE_INFO_MENU_ITEMS = RELEASE_SECTIONS.flatMap((section) =>
  DATASET_BASE_NAMES.map((name, idx) => ({
    section: section.key,
    sectionHeading: idx === 0 ? section.heading : null,
    baseName: name.toLowerCase(),
    label: `${RELEASE_DISPLAY_NAME_YEARS[section.key]} YE ${name}`
  }))
)

/**
 * Index into {@link RELEASE_INFO_MENU_ITEMS} for the Dataset Info popup (non-admin).
 * Maps API `publicationStatus` + nested `dataset` name to the same latest/previous sections as {@link RELEASE_SECTIONS}.
 */
export function getReleaseInfoMenuIndexForPublication(publication) {
  if (!publication) return -1
  const status = (publication.publicationStatus?.name ?? '').toLowerCase()
  const section = status === 'previous release' ? 'previous' : 'latest'
  const baseName = (publication.dataset?.displayName ?? publication.dataset?.name ?? '').toLowerCase()
  return RELEASE_INFO_MENU_ITEMS.findIndex(
    (item) => item.section === section && item.baseName === baseName
  )
}

/**
 * Dataset Info menu index from a non-admin display string (sidebar / Library).
 * Matches a static {@link RELEASE_INFO_MENU_ITEMS} label, or the same text after a leading `YYYY YE`
 * when the API year differs from the FE menu constant.
 */
export function getReleaseInfoMenuIndexForDisplayName(releaseDisplayName) {
  if (!releaseDisplayName || typeof releaseDisplayName !== 'string') return -1
  const r = releaseDisplayName.trim().toLowerCase()
  const exact = RELEASE_INFO_MENU_ITEMS.findIndex((item) => item.label.toLowerCase() === r)
  if (exact >= 0) return exact
  const rSuffix = r.replace(/^\d{4}\s+ye\s+/, '').trim()
  if (!rSuffix) return -1
  const hits = RELEASE_INFO_MENU_ITEMS.map((item, i) => ({
    i,
    s: item.label.toLowerCase().replace(/^\d{4}\s+ye\s+/, '').trim()
  })).filter((x) => x.s === rSuffix)
  if (hits.length >= 1) return hits[0].i
  return -1
}

/**
 * Label for one Dataset Info nav row: API-driven when a matching publication exists, else static menu label.
 */
export function getReleaseInfoMenuItemDisplayLabel(item, publications = []) {
  if (!item) return ''
  const list = Array.isArray(publications) ? publications : []
  const wantPrevious = item.section === 'previous'
  const pub = list.find((p) => {
    const status = (p.publicationStatus?.name ?? '').toLowerCase()
    const inSection = wantPrevious ? status === 'previous release' : status !== 'previous release'
    if (!inSection) return false
    const dn = (p.dataset?.displayName ?? p.dataset?.name ?? '').toLowerCase()
    return dn === item.baseName
  })
  if (pub) return getReleaseDisplayName(pub)
  return item.label
}
